import { useEffect, useState } from "react";
import { Badge, Button, Card, ProgressBar } from "react-bootstrap";
import { FaRedo, FaTrash } from "react-icons/fa";
import { UPLOAD_PROGRESS_EVENT, uploadProgressOf } from "../services/uploadQueue.js";

function QueuedPhotoCard({ record, onRetry, onDiscard }) {
  const [preview, setPreview] = useState("");
  const [percent, setPercent] = useState(() => uploadProgressOf(record.id));

  // Keyed on the record id, not the blob: every queue re-read hands back a fresh
  // Blob object for the same photo, which used to revoke and rebuild the preview
  // (and visibly flicker the thumbnail) on each change.
  useEffect(() => {
    const url = URL.createObjectURL(record.blob);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.id]);

  useEffect(() => {
    setPercent(uploadProgressOf(record.id));
    function onProgress(event) {
      if (event.detail?.id === record.id) setPercent(event.detail.percent);
    }
    window.addEventListener(UPLOAD_PROGRESS_EVENT, onProgress);
    return () => window.removeEventListener(UPLOAD_PROGRESS_EVENT, onProgress);
  }, [record.id]);

  const uploading = record.status === "uploading";
  // A photo held up by an expired session is not the photo's problem — it says so,
  // and it re-arms itself once the inspector signs in again (see uploadQueue.js).
  const needsAuth = record.status === "needs_auth";
  const failed = record.status === "needs_retry" || needsAuth;
  return (
    <Card className="h-100 border-warning">
      <Card.Img variant="top" src={preview} alt={record.filename || "Queued inspection photo"} className="inspection-photo-thumb" />
      <Card.Body className="p-2">
        <div className="d-flex align-items-center justify-content-between gap-2">
          <Badge bg={failed ? "danger" : uploading ? "primary" : "warning"} text={failed || uploading ? undefined : "dark"}>
            {needsAuth ? "Sign in again" : failed ? "Needs retry" : uploading ? "Uploading" : "Waiting"}
          </Badge>
          <div className="d-flex gap-1">
            {failed ? <Button size="sm" variant="outline-primary" onClick={onRetry} data-hint="Retry" aria-label="Retry"><FaRedo /></Button> : null}
            <Button size="sm" variant="outline-danger" onClick={onDiscard} data-hint="Remove from queue" aria-label="Remove from queue"><FaTrash /></Button>
          </div>
        </div>
        {uploading ? (
          // A null percent means the browser could not tell us the total size —
          // show a moving bar rather than a fake number.
          <ProgressBar
            animated
            striped={percent == null}
            now={percent == null ? 100 : percent}
            label={percent == null ? null : `${percent}%`}
            className="mt-2"
          />
        ) : null}
        {record.error ? <div className="small text-danger mt-2">{record.error}</div> : null}
      </Card.Body>
    </Card>
  );
}

export default QueuedPhotoCard;
